import type { SupabaseClient } from '../client'
import type { Database } from '../database.types'
import { unwrapList } from './_helpers'

export type TaskRow = Database['operacoes']['Tables']['tasks']['Row']

const TASK_COLUMNS =
  'id, title, description, status, priority, area, task_type, project_id, initiative_id, sprint_id, module_id, parent_id, assigned_to, due_date, original_due_date, story_points, is_urgent, blocked, blocked_reason, sort_order, tags, qa_status, created_at, updated_at'

export type ListTasksOptions = {
  sprintId?: number | null
  initiativeId?: number | null
  status?: string | string[]
  limit?: number
}

/**
 * Lista tasks de um projeto. Filtros opcionais por sprint, initiative, status.
 * Ordena por sort_order/due_date.
 */
export async function listTasksByProject(
  client: SupabaseClient,
  projectId: number,
  options: ListTasksOptions = {},
): Promise<TaskRow[]> {
  let q = client.schema('operacoes').from('tasks').select(TASK_COLUMNS).eq('project_id', projectId)

  if (options.sprintId !== undefined && options.sprintId !== null) {
    q = q.eq('sprint_id', options.sprintId)
  }
  if (options.initiativeId !== undefined && options.initiativeId !== null) {
    q = q.eq('initiative_id', options.initiativeId)
  }
  if (options.status) {
    const arr = Array.isArray(options.status) ? options.status : [options.status]
    q = q.in('status', arr)
  }

  q = q.order('sort_order', { ascending: true, nullsFirst: false }).order('due_date', {
    ascending: true,
    nullsFirst: false,
  })

  if (options.limit) q = q.limit(options.limit)

  const result = await q
  return unwrapList(result) as unknown as TaskRow[]
}

export type UsuarioLite = {
  id: string
  nome: string
  avatar_url: string | null
}

/**
 * Resolve nomes de usuários (assignees) a partir de uma lista de IDs.
 * Retorna um Map id → { nome, avatar_url }.
 */
export async function getUsuariosByIds(
  client: SupabaseClient,
  ids: string[],
): Promise<Map<string, UsuarioLite>> {
  const unique = [...new Set(ids.filter(Boolean))]
  if (unique.length === 0) return new Map()

  const result = await client
    .schema('shared')
    .from('usuarios')
    .select('id, nome, avatar_url')
    .in('id', unique)

  const rows = unwrapList(result) as unknown as UsuarioLite[]
  return new Map(rows.map((u) => [u.id, u]))
}
